import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Plus, Trash2, Save, Calculator as CalcIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

export const Calculator = () => {
  const navigate = useNavigate();
  const [materials, setMaterials] = useState([]);
  const [laborItems, setLaborItems] = useState([]);
  const [projectName, setProjectName] = useState('');
  const [projectNotes, setProjectNotes] = useState('');
  const [advancePayment, setAdvancePayment] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedMaterial, setSelectedMaterial] = useState('');
  const [selectedLabor, setSelectedLabor] = useState('');
  const [quantity, setQuantity] = useState('');
  const [laborQuantity, setLaborQuantity] = useState('');
  const [activeTab, setActiveTab] = useState('materials'); // 'materials' or 'labor'

  useEffect(() => {
    loadMaterials();
    loadLabor();
  }, []);

  const loadMaterials = () => {
    const stored = localStorage.getItem('materials');
    if (stored) {
      setMaterials(JSON.parse(stored));
    }
  };

  const loadLabor = () => {
    const stored = localStorage.getItem('labor');
    if (stored) {
      setLaborItems(JSON.parse(stored));
    }
  };

  const addItem = () => {
    if (!selectedMaterial || !quantity || parseFloat(quantity) <= 0) {
      toast.error('Lütfen malzeme seçin ve geçerli miktar girin');
      return;
    }

    const material = materials.find(m => m.id === selectedMaterial);
    if (!material) return;

    const qty = parseFloat(quantity);
    const cost = qty * material.pricePerUnit;

    const item = {
      id: Date.now().toString(),
      type: 'material',
      materialId: material.id,
      materialName: material.name,
      category: material.category,
      unit: material.unit,
      pricePerUnit: material.pricePerUnit,
      quantity: qty,
      totalCost: cost,
    };

    setSelectedItems([...selectedItems, item]);
    setSelectedMaterial('');
    setQuantity('');
    toast.success('Malzeme eklendi');
  };

  const addLaborItem = () => {
    if (!selectedLabor || !laborQuantity || parseFloat(laborQuantity) <= 0) {
      toast.error('Lütfen işçilik seçin ve geçerli miktar girin');
      return;
    }

    const labor = laborItems.find(l => l.id === selectedLabor);
    if (!labor) return;

    const qty = parseFloat(laborQuantity);
    const cost = qty * labor.pricePerUnit;

    const item = {
      id: Date.now().toString(),
      type: 'labor',
      materialId: labor.id,
      materialName: labor.name,
      category: labor.category,
      unit: labor.unit,
      pricePerUnit: labor.pricePerUnit,
      quantity: qty,
      totalCost: cost,
    };

    setSelectedItems([...selectedItems, item]);
    setSelectedLabor('');
    setLaborQuantity('');
    toast.success('İşçilik eklendi');
  };

  const removeItem = (id) => {
    setSelectedItems(selectedItems.filter(item => item.id !== id));
    toast.success('Malzeme kaldırıldı');
  };

  const calculateTotal = () => {
    return selectedItems.reduce((sum, item) => sum + item.totalCost, 0);
  };

  const saveProject = () => {
    if (!projectName.trim()) {
      toast.error('Lütfen proje adı girin');
      return;
    }

    if (selectedItems.length === 0) {
      toast.error('Lütfen en az bir kalem ekleyin');
      return;
    }

    const total = calculateTotal();
    const advance = parseFloat(advancePayment) || 0;

    const project = {
      id: Date.now().toString(),
      name: projectName,
      notes: projectNotes,
      items: selectedItems,
      totalCost: total,
      advancePayment: advance,
      remainingPayment: total - advance,
      status: 'active', // 'active' or 'completed'
      createdAt: new Date().toISOString(),
    };

    const projects = JSON.parse(localStorage.getItem('projects') || '[]');
    projects.unshift(project);
    localStorage.setItem('projects', JSON.stringify(projects));

    toast.success('Proje kaydedildi');
    
    // Reset form
    setProjectName('');
    setProjectNotes('');
    setAdvancePayment('');
    setSelectedItems([]);
    
    // Navigate to projects after a short delay
    setTimeout(() => navigate('/projects'), 1000);
  };

  const totalCost = calculateTotal();
  const advance = parseFloat(advancePayment) || 0;
  const remaining = totalCost - advance;

  return (
    <div className="p-8 animate-in">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-foreground mb-2">Maliyet Hesaplama</h1>
        <p className="text-muted-foreground text-lg">Yeni bir proje maliyeti hesaplayın</p>
      </div>

      {/* Project Info */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Proje Bilgileri</CardTitle>
          <CardDescription>Hesaplamanız için bilgileri girin</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="projectName">Proje Adı *</Label>
            <Input
              id="projectName"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              placeholder="Örn: Villa Elektrik İşleri"
              className="mt-1.5"
            />
          </div>
          <div>
            <Label htmlFor="projectNotes">Proje Notları</Label>
            <Input
              id="projectNotes"
              value={projectNotes}
              onChange={(e) => setProjectNotes(e.target.value)}
              placeholder="Opsiyonel notlar veya açıklamalar"
              className="mt-1.5"
            />
          </div>
          <div>
            <Label htmlFor="advancePayment">Ön Ödeme (₺)</Label>
            <Input
              id="advancePayment"
              type="number"
              step="0.01"
              value={advancePayment}
              onChange={(e) => setAdvancePayment(e.target.value)}
              placeholder="0.00"
              className="mt-1.5 max-w-xs"
            />
          </div>
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Add Material/Labor Section */}
        <div className="lg:col-span-2">
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Plus className="h-5 w-5 text-primary" />
                Malzeme ve İşçilik Ekle
              </CardTitle>
              <CardDescription>Kullanacağınız malzemeleri ve işçilikleri seçin</CardDescription>
            </CardHeader>
            <CardContent>
              {/* Tabs */}
              <div className="flex gap-2 mb-6 border-b border-border">
                <button
                  onClick={() => setActiveTab('materials')}
                  className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
                    activeTab === 'materials'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Malzemeler
                </button>
                <button
                  onClick={() => setActiveTab('labor')}
                  className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
                    activeTab === 'labor'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  İşçilik
                </button>
              </div>

              {/* Materials Tab */}
              {activeTab === 'materials' && (
                materials.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-muted-foreground mb-4">Henüz malzeme bulunmuyor</p>
                    <Button onClick={() => navigate('/materials')} variant="outline">
                      Malzeme Ekle
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="material">Malzeme</Label>
                      <Select value={selectedMaterial} onValueChange={setSelectedMaterial}>
                        <SelectTrigger id="material" className="mt-1.5">
                          <SelectValue placeholder="Malzeme seçin" />
                        </SelectTrigger>
                        <SelectContent>
                          {materials.map(material => (
                            <SelectItem key={material.id} value={material.id}>
                              {material.name} - ₺{material.pricePerUnit.toFixed(2)}/{material.unit}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="quantity">Miktar</Label>
                      <Input
                        id="quantity"
                        type="number"
                        step="0.01"
                        value={quantity}
                        onChange={(e) => setQuantity(e.target.value)}
                        placeholder="Örn: 100"
                        className="mt-1.5"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addItem();
                          }
                        }}
                      />
                    </div>

                    <Button onClick={addItem} className="w-full bg-primary hover:bg-primary/90">
                      <Plus className="mr-2 h-4 w-4" />
                      Malzeme Ekle
                    </Button>
                  </div>
                )
              )}

              {/* Labor Tab */}
              {activeTab === 'labor' && (
                laborItems.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-muted-foreground mb-4">Henüz işçilik bulunmuyor</p>
                    <Button onClick={() => navigate('/labor')} variant="outline">
                      İşçilik Ekle
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="labor">İşçilik</Label>
                      <Select value={selectedLabor} onValueChange={setSelectedLabor}>
                        <SelectTrigger id="labor" className="mt-1.5">
                          <SelectValue placeholder="İşçilik seçin" />
                        </SelectTrigger>
                        <SelectContent>
                          {laborItems.map(labor => (
                            <SelectItem key={labor.id} value={labor.id}>
                              {labor.name} - ₺{labor.pricePerUnit.toFixed(2)}/{labor.unit}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="laborQuantity">Miktar</Label>
                      <Input
                        id="laborQuantity"
                        type="number"
                        step="0.01"
                        value={laborQuantity}
                        onChange={(e) => setLaborQuantity(e.target.value)}
                        placeholder="Örn: 5"
                        className="mt-1.5"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addLaborItem();
                          }
                        }}
                      />
                    </div>

                    <Button onClick={addLaborItem} className="w-full bg-accent hover:bg-accent/90">
                      <Plus className="mr-2 h-4 w-4" />
                      İşçilik Ekle
                    </Button>
                  </div>
                )
              )}
            </CardContent>
          </Card>

          {/* Selected Items */}
          <Card>
            <CardHeader>
              <CardTitle>Seçilen Kalemler</CardTitle>
              <CardDescription>Hesaplamaya dahil edilen malzemeler ve işçilikler</CardDescription>
            </CardHeader>
            <CardContent>
              {selectedItems.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  Henüz kalem eklenmedi
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Tip</TableHead>
                        <TableHead>Ad</TableHead>
                        <TableHead>Kategori</TableHead>
                        <TableHead className="text-right">Miktar</TableHead>
                        <TableHead className="text-right">Birim Fiyat</TableHead>
                        <TableHead className="text-right">Toplam</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedItems.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>
                            <Badge variant={item.type === 'labor' ? 'default' : 'outline'}>
                              {item.type === 'labor' ? 'İşçilik' : 'Malzeme'}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-medium">{item.materialName}</TableCell>
                          <TableCell>
                            <Badge variant="secondary">{item.category}</Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            {item.quantity} {item.unit}
                          </TableCell>
                          <TableCell className="text-right">
                            ₺{item.pricePerUnit.toFixed(2)}
                          </TableCell>
                          <TableCell className="text-right font-semibold text-primary">
                            ₺{item.totalCost.toFixed(2)}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeItem(item.id)}
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Summary */}
        <div className="lg:col-span-1">
          <Card className="sticky top-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CalcIcon className="h-5 w-5 text-accent" />
                Özet
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-muted-foreground">Toplam Kalem</span>
                  <span className="font-medium">{selectedItems.length}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Toplam Miktar</span>
                  <span className="font-medium">
                    {selectedItems.reduce((sum, item) => sum + item.quantity, 0).toFixed(2)}
                  </span>
                </div>
              </div>

              <div className="pt-4 border-t border-border space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">Toplam Maliyet</span>
                  <span className="text-xl font-bold text-foreground">
                    ₺{totalCost.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
                
                {advance > 0 && (
                  <>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-muted-foreground">Ön Ödeme</span>
                      <span className="font-medium text-primary">
                        -₺{advance.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                    <div className="flex justify-between items-center pt-2 border-t border-border">
                      <span className="text-sm font-medium">Kalan Ödeme</span>
                      <span className="text-2xl font-bold text-accent">
                        ₺{remaining.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  </>
                )}
              </div>

              <Button 
                onClick={saveProject} 
                disabled={selectedItems.length === 0 || !projectName.trim()}
                className="w-full bg-accent hover:bg-accent/90"
              >
                <Save className="mr-2 h-4 w-4" />
                Projeyi Kaydet
              </Button>

              <p className="text-xs text-muted-foreground text-center">
                Proje kaydedildikten sonra Projeler sayfasından görüntüleyebilirsiniz
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Calculator;